import React from 'react';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputAdornment from '@mui/material/InputAdornment';
import { Controller, useFormContext } from 'react-hook-form';

/**
 * FormField — responsive Grid item that renders an RHF-controlled MUI input.
 *
 * Must be used inside a <Form> (which provides FormProvider context) and
 * inside a <FormRow> (which provides the Grid container).
 *
 * Breakpoint props (xs, sm, md, lg, xl) control the column width at each
 * breakpoint. xs={12} is the implicit default — fields stack full-width on
 * phones unless you say otherwise.
 *
 * Supported types:
 *   text        — TextField (default)
 *   number      — TextField type="number"
 *   textarea    — TextField multiline rows={3}
 *   select      — TextField select, requires `options` prop
 *   checkbox    — Checkbox + FormControlLabel
 *   currency    — TextField with $ InputAdornment, type="number"
 *
 * Always size="small" per convention (override with sizeProp if needed).
 *
 * Usage:
 *   <FormField name="name"   label="Name"   xs={12} md={8} />
 *   <FormField name="type"   label="Type"   type="select" options={typeOptions} xs={12} md={4} />
 *   <FormField name="active" label="Active" type="checkbox" xs={12} md={6} />
 *   <FormField name="amount" label="Amount" type="currency" xs={12} md={4} />
 *
 * Props:
 *   name        {string}   — RHF field name, supports dot-path for nested/array
 *   label       {string}   — input label
 *   type        {string}   — 'text'|'number'|'textarea'|'select'|'checkbox'|'currency'
 *   options     {array}    — [{ label, value }] required for type="select"
 *   required    {boolean}
 *   disabled    {boolean}
 *   sizeProp    {string}   — MUI size prop (default 'small')
 *   xs,sm,md,lg,xl         — Grid breakpoint column widths
 *   gridSx      {object}   — sx on the Grid item wrapper
 *   ...rest                — forwarded to the underlying MUI input
 */
export default function FormField({
  name,
  label,
  type = 'text',
  options = [],
  required = false,
  disabled = false,
  sizeProp = 'small',
  // Grid breakpoints
  xs = 12,
  sm,
  md,
  lg,
  xl,
  gridSx,
  ...rest
}) {
  const { control } = useFormContext();

  return (
    <Grid item xs={xs} sm={sm} md={md} lg={lg} xl={xl} sx={gridSx}>
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => {
          const errorMsg = error?.message ?? null;
          const isError = !!error;

          // ── checkbox ──────────────────────────────────────────────────────
          if (type === 'checkbox') {
            return (
              <FormControl error={isError} disabled={disabled}>
                <FormControlLabel
                  label={label}
                  control={
                    <Checkbox
                      {...field}
                      checked={!!field.value}
                      size={sizeProp}
                    />
                  }
                />
                {errorMsg && <FormHelperText>{errorMsg}</FormHelperText>}
              </FormControl>
            );
          }

          // ── select ────────────────────────────────────────────────────────
          if (type === 'select') {
            return (
              <TextField
                {...field}
                select
                fullWidth
                label={label}
                required={required}
                disabled={disabled}
                size={sizeProp}
                error={isError}
                helperText={errorMsg}
                {...rest}
              >
                {/* Blank sentinel so the field can be cleared */}
                <MenuItem value=""><em>—</em></MenuItem>
                {options.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            );
          }

          // ── currency ──────────────────────────────────────────────────────
          if (type === 'currency') {
            return (
              <TextField
                {...field}
                fullWidth
                label={label}
                required={required}
                disabled={disabled}
                size={sizeProp}
                type="number"
                error={isError}
                helperText={errorMsg}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                {...rest}
              />
            );
          }

          // ── textarea ──────────────────────────────────────────────────────
          if (type === 'textarea') {
            return (
              <TextField
                {...field}
                fullWidth
                label={label}
                required={required}
                disabled={disabled}
                size={sizeProp}
                multiline
                rows={3}
                error={isError}
                helperText={errorMsg}
                {...rest}
              />
            );
          }

          // ── text / number (default) ───────────────────────────────────────
          return (
            <TextField
              {...field}
              fullWidth
              label={label}
              required={required}
              disabled={disabled}
              size={sizeProp}
              type={type === 'number' ? 'number' : 'text'}
              error={isError}
              helperText={errorMsg}
              {...rest}
            />
          );
        }}
      />
    </Grid>
  );
}
